import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

interface UseRouteChangeRefreshOptions {
  hasUpdate: boolean;
  enabled?: boolean;
}

/**
 * Hook que detecta mudanças de rota e faz refresh automático
 * se existe uma atualização pendente.
 * 
 * Isso garante que o usuário sempre veja a versão mais recente
 * ao navegar entre páginas.
 */
export function useRouteChangeRefresh({ 
  hasUpdate, 
  enabled = true 
}: UseRouteChangeRefreshOptions): void {
  const location = useLocation();
  const previousPathRef = useRef<string>(location.pathname);
  const isFirstRenderRef = useRef(true);

  useEffect(() => {
    // Ignora a primeira renderização
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      previousPathRef.current = location.pathname;
      return;
    }

    // Só atua se habilitado e houver update
    if (!enabled || !hasUpdate) {
      previousPathRef.current = location.pathname;
      return;
    }

    // Detecta mudança de rota (não apenas query params)
    const previousPath = previousPathRef.current;
    const currentPath = location.pathname;

    if (previousPath !== currentPath) {
      console.log('[RouteChangeRefresh] Navegação detectada com update pendente. Recarregando...');
      
      // Pequeno delay para garantir que a navegação foi registrada
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }

    previousPathRef.current = location.pathname;
  }, [location.pathname, hasUpdate, enabled]);
}
